'use client';

import React from 'react';
import { FileDownloadOutlinedIcon } from '@/components/icons';
import { IconButton } from '@mui/material';
import type { ChatMessage } from './ChatBot';
import { trackWidgetEvent } from '../utils/analytics';
import {
  CHAT_TRANSCRIPT_DOWNLOAD_CLICKED,
  CHAT_TRANSCRIPT_DOWNLOAD_FAILED,
  CHAT_TRANSCRIPT_DOWNLOAD_FALLBACK,
  CHAT_TRANSCRIPT_DOWNLOAD_SUCCEEDED,
} from '@/lib/analytics-events';
import { buildTranscriptDownload, canShowTranscriptDownload } from './generateTranscript';
import { deliverTranscript } from './deliverTranscript';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

interface ChatHeaderMenuProps {
  transcriptDownloadEnabled: boolean;
  institutionName: string;
  agentName: string;
  history: ChatMessage[];
  sessionOpenedAt: number | null;
  onTranscriptError: () => void;
  fontScale: (size: number) => string | number;
}

export function ChatHeaderMenu({
  transcriptDownloadEnabled,
  institutionName,
  agentName,
  history,
  sessionOpenedAt,
  onTranscriptError,
  fontScale,
}: ChatHeaderMenuProps) {
  const { t, locale } = useWidgetI18n();
  const showDownload = canShowTranscriptDownload(transcriptDownloadEnabled);
  if (!showDownload) return null;

  const completedCount = history.filter((msg) => Boolean(msg.text?.trim())).length;

  const handleDownload = () => {
    const sessionDurationSec =
      sessionOpenedAt != null
        ? Math.max(0, Math.round((Date.now() - sessionOpenedAt) / 1000))
        : undefined;
    trackWidgetEvent(CHAT_TRANSCRIPT_DOWNLOAD_CLICKED, {
      channel: 'web',
      message_count: completedCount,
      ...(sessionDurationSec !== undefined && { session_duration_sec: sessionDurationSec }),
    });

    try {
      const { content, filename } = buildTranscriptDownload({
        institutionName,
        agentName,
        messages: history,
      });
      const result = deliverTranscript({ content, filename });

      if (result === 'fallback') {
        trackWidgetEvent(CHAT_TRANSCRIPT_DOWNLOAD_FALLBACK, { channel: 'web' });
      } else {
        trackWidgetEvent(CHAT_TRANSCRIPT_DOWNLOAD_SUCCEEDED, {
          channel: 'web',
          message_count: completedCount,
          format: 'txt',
        });
      }
    } catch {
      trackWidgetEvent(CHAT_TRANSCRIPT_DOWNLOAD_FAILED, {
        channel: 'web',
        failure_reason: 'generation_or_delivery',
      });
      onTranscriptError();
    }
  };

  return (
    <IconButton
      size="small"
      aria-label={t('drawer.downloadTranscript')}
      title={t('drawer.downloadTranscript')}
      lang={locale}
      sx={{ color: 'inherit' }}
      onClick={handleDownload}
    >
      <FileDownloadOutlinedIcon fontSize="small" sx={{ fontSize: fontScale(16) }} />
    </IconButton>
  );
}
